/**
 * EmailActionsPanel.js
 * M-09: Invitation, Reminder (with daily limit), and Policy diffusion buttons per center.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mail, Bell, FileText, Loader2, Clock, CheckCircle } from 'lucide-react';

const EmailActionsPanel = ({ empresaId, centroId, centroNombre, language }) => {
  const [reminderStatus, setReminderStatus] = useState({ can_send: true, message: null });
  const [sendingInvite, setSendingInvite] = useState(false);
  const [sendingReminder, setSendingReminder] = useState(false);
  const [sendingPolicy, setSendingPolicy] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const t = language === 'es' ? {
    invite: 'Enviar invitación',
    reminder: 'Enviar recordatorio',
    policy: 'Difundir política',
    sending: 'Enviando...',
    sent: 'enviados',
    allComplete: 'Todos completaron',
    limitReached: 'Ya enviaste un recordatorio hoy. Podrás enviar otro mañana.',
  } : {
    invite: 'Send invitation',
    reminder: 'Send reminder',
    policy: 'Diffuse policy',
    sending: 'Sending...',
    sent: 'sent',
    allComplete: 'All completed',
    limitReached: 'You already sent a reminder today. You can send another tomorrow.',
  };

  const checkReminderStatus = useCallback(async () => {
    if (!centroId) return;
    try {
      const res = await axios.get(`/v5/nom035/${empresaId}/email/reminder-status/${centroId}`);
      if (res.data?.success) {
        setReminderStatus(res.data.data);
      }
    } catch { /* silent */ }
  }, [empresaId, centroId]);

  useEffect(() => { checkReminderStatus(); }, [checkReminderStatus]);

  const handleInvite = async () => {
    setSendingInvite(true);
    setLastResult(null);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/email/invite/${centroId}`);
      if (res.data?.success) {
        const d = res.data.data;
        toast.success(`${d.enviados} ${t.sent} — ${centroNombre}`);
        setLastResult({ type: 'invite', ...d });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSendingInvite(false); }
  };

  const handleReminder = async () => {
    setSendingReminder(true);
    setLastResult(null);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/email/reminder/${centroId}`);
      if (res.data?.success) {
        const d = res.data.data;
        if (d.enviados === 0) {
          toast.info(d.message || t.allComplete);
        } else {
          toast.success(`${d.enviados} ${t.sent} — ${centroNombre}`);
        }
        setLastResult({ type: 'reminder', ...d });
        setReminderStatus({ can_send: false, message: t.limitReached });
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Error';
      if (err.response?.status === 429) {
        setReminderStatus({ can_send: false, message: detail });
        toast.warning(detail);
      } else {
        toast.error(detail);
      }
    } finally { setSendingReminder(false); }
  };

  const handlePolicy = async () => {
    setSendingPolicy(true);
    setLastResult(null);
    try {
      const res = await axios.post(`/v5/nom035/${empresaId}/email/policy/${centroId}`);
      if (res.data?.success) {
        const d = res.data.data;
        toast.success(`${d.enviados} ${t.sent} — ${centroNombre}`);
        setLastResult({ type: 'policy', ...d });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSendingPolicy(false); }
  };

  return (
    <div className="border-t pt-3 mt-1 space-y-2" data-testid="m09-email-actions">
      <p className="text-xs font-medium text-gray-500 mb-2">
        {language === 'es' ? 'Acciones de correo' : 'Email Actions'}
      </p>
      <div className="flex flex-wrap gap-2">
        {/* Invitation */}
        <Button size="sm" variant="outline" onClick={handleInvite} disabled={sendingInvite}
          className="text-teal-700 border-teal-300 hover:bg-teal-50" data-testid="m09-btn-invite">
          {sendingInvite ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Mail className="h-3.5 w-3.5 mr-1.5" />}
          {sendingInvite ? t.sending : t.invite}
        </Button>

        {/* Reminder (daily limit) */}
        <Button size="sm" variant="outline" onClick={handleReminder}
          disabled={sendingReminder || !reminderStatus.can_send}
          className={reminderStatus.can_send
            ? 'text-orange-700 border-orange-300 hover:bg-orange-50'
            : 'text-gray-400 border-gray-200 cursor-not-allowed'}
          data-testid="m09-btn-reminder">
          {sendingReminder
            ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />
            : !reminderStatus.can_send
              ? <Clock className="h-3.5 w-3.5 mr-1.5" />
              : <Bell className="h-3.5 w-3.5 mr-1.5" />
          }
          {sendingReminder ? t.sending : t.reminder}
        </Button>

        {/* Policy diffusion */}
        <Button size="sm" variant="outline" onClick={handlePolicy} disabled={sendingPolicy}
          className="text-purple-700 border-purple-300 hover:bg-purple-50" data-testid="m09-btn-policy">
          {sendingPolicy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <FileText className="h-3.5 w-3.5 mr-1.5" />}
          {sendingPolicy ? t.sending : t.policy}
        </Button>
      </div>

      {/* Daily limit message */}
      {!reminderStatus.can_send && reminderStatus.message && (
        <p className="text-xs text-amber-600 flex items-center gap-1.5" data-testid="m09-limit-msg">
          <Clock className="h-3 w-3" />
          {reminderStatus.message}
        </p>
      )}

      {/* Last result */}
      {lastResult && (
        <div className="flex items-center gap-2 text-xs text-green-600" data-testid="m09-last-result">
          <CheckCircle className="h-3.5 w-3.5" />
          <span>{lastResult.enviados} {t.sent}</span>
          {lastResult.estado && <Badge variant="outline" className="text-xs px-1.5 py-0">{lastResult.estado}</Badge>}
        </div>
      )}
    </div>
  );
};

export default EmailActionsPanel;
